declare global {
  interface Window {
    RELAYRO_CONFIG?: {
      apiBaseUrl?: string;
    };
  }
}

export interface School {
  slug: string;
  name: string;
  contactEmail?: string;
  contactPhone?: string;
  brandColor?: string;
  accentColor?: string;
  logoUrl?: string;
  currency: "AUD";
  gstIncluded: boolean;
  stripeConfigured: boolean;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
}

export interface Term {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
}

export interface ClassTemplate {
  id: string;
  name: string;
  level?: string;
  ageGroup?: string;
  description?: string;
  defaultCapacity?: number;
  defaultPriceCents?: number;
  defaultDurationMinutes?: number;
  defaultVenueId?: string;
  defaultCoachPhone?: string;
  nextTemplateId?: string;
  sortOrder?: number;
  active?: boolean;
}

export interface ClassItem {
  id: string;
  name: string;
  templateId?: string;
  templateName?: string;
  nextTemplateId?: string;
  termId: string;
  termName?: string;
  venueId: string;
  venueName?: string;
  venueAddress?: string;
  coachPhone?: string;
  schedule: string;
  startsAt?: string;
  capacity: number;
  priceCents: number;
  currency: "AUD";
  gstIncluded: boolean;
  level?: string;
  ageGroup?: string;
}

export interface HolidayProgramBlock {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  priceCents?: number;
  open?: boolean;
  bookedCount?: number;
}

export interface HolidayProgram {
  id: string;
  name: string;
  venueId: string;
  venueName?: string;
  venueAddress?: string;
  coachPhone?: string;
  startsAt: string;
  endsAt: string;
  level?: string;
  ageGroup?: string;
  description?: string;
  blockPriceCents: number;
  currency: "AUD";
  gstIncluded: boolean;
  blocks: HolidayProgramBlock[];
}

export interface StaffMember {
  userKey?: string;
  phone: string;
  email?: string;
  name?: string;
  role: "admin" | "coach" | "finance";
}

export interface CoachCoverage {
  id: string;
  targetType: "class" | "holiday";
  classId?: string;
  programId?: string;
  blockId?: string;
  coachPhone: string;
  coachName?: string;
  targetName?: string;
  blockLabel?: string;
  date: string;
  note?: string;
}

export interface Student {
  id: string;
  name: string;
  dateOfBirth: string;
  age?: string;
  allergies?: string;
  healthNotes?: string;
  emergencyContact?: {
    name?: string;
    phone?: string;
  };
  parent?: {
    name?: string;
    email?: string;
  };
}

export interface Registration {
  id: string;
  classId?: string;
  programId?: string;
  programType?: "class" | "holiday";
  studentId?: string;
  venueId?: string;
  guardianPhone?: string;
  guardianEmail?: string;
  parent?: {
    name?: string;
    email?: string;
  };
  emergencyContact?: {
    name?: string;
    phone?: string;
  };
  status: "confirmed" | "waitlist" | "cancelled";
  paymentStatus: string;
  priceCents: number;
  paymentMethod?: string;
  paymentNote?: string;
  amountPaidCents?: number;
  paidAt?: string;
  createdAt?: string;
  reEnrollmentCampaignId?: string;
  coachRole?: "primary" | "substitute";
  coachSessionDate?: string;
  coachBlockId?: string;
  selectedBlocks?: HolidayProgramBlock[];
  student: {
    id?: string;
    name: string;
    dateOfBirth: string;
    age?: string;
    allergies?: string;
    healthNotes?: string;
  };
  attendanceSummary?: {
    present: number;
    absent: number;
    leave: number;
  };
}

export function getApiBaseUrl() {
  const configured = window.RELAYRO_CONFIG?.apiBaseUrl?.replace(/\/$/, "");
  if (configured) return configured;
  return import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "";
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("relayro.session");
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {})
    }
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) throw new Error(data.error ?? "Request failed");
  return data as T;
}

export function formatAud(cents: number) {
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD"
  });
}
